import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { StockBadge, ExpiryBadge, FefoBadge } from "@/components/StatusBadges";
import ExportDialog from "@/components/ExportDialog";
import { ChevronDown, ChevronRight, Search, Thermometer, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Fragment } from "react";

const STORAGE_ICON = { "2-8C": "#0a8f8f", Frozen: "#3B82F6", "Room Temp": "#64748B" };

export default function Inventory() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ["inventory"],
    queryFn: () => api.get("/inventory").then((r) => r.data),
  });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.get("/categories").then((r) => r.data) });

  // Editing/deleting here corrects the underlying stock batch (GRN lot entry) a row's
  // total is built from — the Inventory table itself is just a rolled-up view, so
  // "edit/delete an inventory entry" means fixing or removing the batch behind it.
  const blankBatchForm = { lot_number: "", mfg_date: "", expiry_date: "", vendor_name: "", grn_ref: "" };
  const [editBatch, setEditBatch] = useState(null);
  const [batchForm, setBatchForm] = useState(blankBatchForm);
  const [deleteBatch, setDeleteBatch] = useState(null);

  const openEditBatch = (b) => {
    setEditBatch(b);
    setBatchForm({
      lot_number: b.lot_number || "", mfg_date: b.mfg_date || "", expiry_date: b.expiry_date || "",
      vendor_name: b.vendor_name || "", grn_ref: b.grn_ref || "",
    });
  };

  const saveEditBatch = async () => {
    if (!editBatch) return;
    try {
      await api.put(`/batches/${editBatch._id}`, batchForm);
      toast.success("Stock entry updated");
      setEditBatch(null);
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmDeleteBatch = async () => {
    if (!deleteBatch) return;
    try {
      await api.delete(`/batches/${deleteBatch._id}`);
      toast.success("Stock entry deleted");
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDeleteBatch(null);
  };

  const filtered = data.filter(
    (r) =>
      r.item_name.toLowerCase().includes(search.toLowerCase()) &&
      (cat === "all" || r.category === cat)
  );

  return (
    <div className="space-y-5" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-navy">Inventory &amp; Stock Ledger</h2>
          <p className="text-slate-500 text-sm mt-1">Location-wise stock with batch/lot expiry &amp; FEFO enforcement.</p>
        </div>
        <ExportDialog dataset="inventory" testid="inventory-export" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="inventory-search"
            placeholder="Search items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white"
          />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="inventory-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.filter((c) => !["equipment_calibrated", "equipment_asset"].includes(c.field_group)).map((c) => <SelectItem key={c._id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold w-8"></th>
                <th className="px-4 py-3 font-semibold">Item</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Storage</th>
                <th className="px-4 py-3 font-semibold text-right">Qty</th>
                <th className="px-4 py-3 font-semibold">Stock</th>
                <th className="px-4 py-3 font-semibold">Nearest Expiry</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No inventory found.</td></tr>
              )}
              {filtered.map((r, idx) => {
                const key = `${r.item_id}-${r.location_id}`;
                const isOpen = expanded === key;
                return (
                  <Fragment key={key}>
                    <tr
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-200"
                      onClick={() => setExpanded(isOpen ? null : key)}
                      data-testid={`inventory-row-${idx}`}
                    >
                      <td className="px-4 py-3 text-slate-400">
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </td>
                      <td className="px-4 py-3 font-medium text-navy">{r.item_name}</td>
                      <td className="px-4 py-3 text-slate-600">{r.category}</td>
                      <td className="px-4 py-3 text-slate-600">{r.location_name}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: STORAGE_ICON[r.storage_condition] || "#64748B" }}>
                          <Thermometer size={13} /> {r.storage_condition}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-navy">{r.total_qty} <span className="text-slate-400 font-normal">{r.uom}</span></td>
                      <td className="px-4 py-3"><StockBadge status={r.stock_status} /></td>
                      <td className="px-4 py-3">
                        {r.nearest_expiry ? <ExpiryBadge state={r.expiry_state} days={r.days_to_expiry} /> : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60" data-testid={`inventory-batches-${idx}`}>
                        <td></td>
                        <td colSpan={7} className="px-4 py-3">
                          <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Batches (FEFO order)</div>
                          <div className="space-y-1.5">
                            {r.batches.filter((b) => b.qty_available > 0).map((b, bidx) => (
                              <div
                                key={b._id}
                                className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg bg-white border"
                                style={{ borderColor: b.is_fefo ? "#10B981" : "#e2e8f0", borderWidth: b.is_fefo ? 2 : 1 }}
                                data-testid={`inventory-batch-${idx}-${bidx}`}
                              >
                                <span className="font-mono text-xs text-navy font-semibold">{b.lot_number}</span>
                                {b.is_fefo && <FefoBadge />}
                                <span className="text-xs text-slate-500">Exp: {b.expiry_date}</span>
                                <ExpiryBadge state={b.expiry_state} days={b.days_to_expiry} />
                                <span className="text-xs text-slate-500">Vendor: {b.vendor_name || "—"}</span>
                                <span className="text-xs font-semibold text-navy">{b.qty_available} {r.uom}</span>
                                <span className="ml-auto flex items-center gap-1">
                                  <Button
                                    variant="ghost" size="icon"
                                    onClick={(e) => { e.stopPropagation(); openEditBatch(b); }}
                                    data-testid={`inventory-batch-edit-${idx}-${bidx}`}
                                    className="h-7 w-7 text-slate-400 hover:text-teal hover:bg-slate-100"
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button
                                    variant="ghost" size="icon"
                                    onClick={(e) => { e.stopPropagation(); setDeleteBatch(b); }}
                                    data-testid={`inventory-batch-delete-${idx}-${bidx}`}
                                    className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editBatch} onOpenChange={(o) => !o && setEditBatch(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Edit Stock Entry</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2 mb-1">
            Corrects a mistaken GRN entry — lot number, dates, vendor, or reference.
            To change the quantity itself, use Stock Adjust instead so the change stays on record.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Lot / Batch No.</Label><Input data-testid="edit-batch-lot" value={batchForm.lot_number} onChange={(e) => setBatchForm({ ...batchForm, lot_number: e.target.value })} /></div>
            <div><Label>Vendor</Label><Input data-testid="edit-batch-vendor" value={batchForm.vendor_name} onChange={(e) => setBatchForm({ ...batchForm, vendor_name: e.target.value })} /></div>
            <div><Label>Mfg Date</Label><Input type="date" value={batchForm.mfg_date} onChange={(e) => setBatchForm({ ...batchForm, mfg_date: e.target.value })} /></div>
            <div><Label>Expiry Date</Label><Input type="date" data-testid="edit-batch-expiry" value={batchForm.expiry_date} onChange={(e) => setBatchForm({ ...batchForm, expiry_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>GRN Reference</Label><Input value={batchForm.grn_ref} onChange={(e) => setBatchForm({ ...batchForm, grn_ref: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={saveEditBatch} data-testid="edit-batch-submit" style={{ background: "#042C53" }} className="text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteBatch} onOpenChange={(o) => !o && setDeleteBatch(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this stock entry?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes lot {deleteBatch?.lot_number} entirely — it cannot be undone. Only
              allowed while none of this stock has been consumed, adjusted, or issued yet
              (i.e. it's still a clean, unused GRN entry). If it's already been used,
              delete will be blocked — use Stock Adjust to correct it instead so the
              movement stays on record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="batch-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteBatch} data-testid="batch-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
